<script setup>
import { computed, ref, watch } from 'vue';

import MessageMeta from '../MessageMeta.vue';
import Icon from 'next/icon/Icon.vue';

import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { ATTACHMENT_TYPES, MESSAGE_VARIANTS, ORIENTATION } from '../constants';

const props = defineProps({
  hideMeta: { type: Boolean, default: false },
});

const { variant, orientation, inReplyTo, shouldGroupWithNext, conversationId } =
  useMessageContext();
const { t, te } = useI18n();

// Tipos que rendem miniatura de verdade na citacao; o resto cai no icone.
const THUMBNAIL_TYPES = [
  ATTACHMENT_TYPES.IMAGE,
  ATTACHMENT_TYPES.VIDEO,
  ATTACHMENT_TYPES.IG_REEL,
];

// Equivalente ao ATTACHMENT_ICONS (fluent) usado no MessagePreview, traduzido
// para o icon set lucide do design system novo (components-next).
const REPLY_ATTACHMENT_ICONS = {
  [ATTACHMENT_TYPES.IMAGE]: 'i-lucide-image',
  [ATTACHMENT_TYPES.AUDIO]: 'i-lucide-audio-lines',
  [ATTACHMENT_TYPES.VIDEO]: 'i-lucide-video',
  [ATTACHMENT_TYPES.IG_REEL]: 'i-lucide-video',
  [ATTACHMENT_TYPES.FILE]: 'i-lucide-file',
  [ATTACHMENT_TYPES.LOCATION]: 'i-lucide-map-pin',
  [ATTACHMENT_TYPES.CONTACT]: 'i-lucide-contact',
  [ATTACHMENT_TYPES.FALLBACK]: 'i-lucide-link',
};

const varaintBaseMap = {
  [MESSAGE_VARIANTS.AGENT]: 'bg-n-solid-blue text-n-slate-12',
  [MESSAGE_VARIANTS.PRIVATE]:
    'bg-n-solid-amber text-n-amber-12 [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]: 'bg-n-slate-4 text-n-slate-12',
  [MESSAGE_VARIANTS.ACTIVITY]: 'bg-n-alpha-1 text-n-slate-11 text-sm',
  [MESSAGE_VARIANTS.BOT]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.TEMPLATE]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.ERROR]: 'bg-n-ruby-4 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-solid-amber/70 border border-dashed border-n-amber-12 text-n-amber-12',
};

const orientationMap = {
  [ORIENTATION.LEFT]:
    'left-bubble rounded-xl ltr:rounded-bl-sm rtl:rounded-br-sm',
  [ORIENTATION.RIGHT]:
    'right-bubble rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm',
  [ORIENTATION.CENTER]: 'rounded-md',
};

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const messageClass = computed(() => {
  const classToApply = [varaintBaseMap[variant.value]];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[orientation.value]);
  } else {
    classToApply.push('rounded-lg');
  }

  return classToApply;
});

const shouldShowMeta = computed(
  () =>
    !props.hideMeta &&
    !shouldGroupWithNext.value &&
    variant.value !== MESSAGE_VARIANTS.ACTIVITY
);

// A citada chega em camelCase quando vem embutida no payload (in_reply_to_message)
// e em snake_case quando vem de um objeto so parcialmente convertido; aceitamos
// as duas formas em todo lugar.
const replyAttachment = computed(() => {
  const attachments = inReplyTo.value?.attachments;
  return attachments?.length ? attachments[0] : null;
});

const replyFileType = computed(() => {
  const attachment = replyAttachment.value;
  if (!attachment) return null;

  return attachment.fileType ?? attachment.file_type ?? null;
});

const replyThumbnailUrl = computed(() => {
  const attachment = replyAttachment.value;
  if (!attachment || !THUMBNAIL_TYPES.includes(replyFileType.value)) return '';

  const thumbUrl = attachment.thumbUrl ?? attachment.thumb_url ?? '';
  // Video/reel so tem miniatura se o thumb existir: o data_url e o arquivo de
  // video, que nao renderiza em <img>.
  if (replyFileType.value !== ATTACHMENT_TYPES.IMAGE) return thumbUrl;

  return thumbUrl || attachment.dataUrl || attachment.data_url || '';
});

const hasThumbnailError = ref(false);
watch(replyThumbnailUrl, () => {
  hasThumbnailError.value = false;
});

const showThumbnail = computed(
  () => !!replyThumbnailUrl.value && !hasThumbnailError.value
);

const isVideoThumbnail = computed(
  () => showThumbnail.value && replyFileType.value !== ATTACHMENT_TYPES.IMAGE
);

const replyIcon = computed(() => {
  if (showThumbnail.value) return null;

  return REPLY_ATTACHMENT_ICONS[replyFileType.value] ?? null;
});

const replySenderName = computed(() => {
  const reply = inReplyTo.value;
  if (!reply) return '';

  return reply.senderName ?? reply.sender_name ?? reply.sender?.name ?? '';
});

const replyToPreview = computed(() => {
  const content = inReplyTo.value?.content;
  if (!content) return '';

  return new MessageFormatter(content).formattedMessage;
});

// Rotulo mostrado quando nao ha texto: "Mensagem de imagem", "Mensagem de audio"...
const replyToLabel = computed(() => {
  const fileType = replyFileType.value;
  if (!fileType) return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');

  const key = `CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`;
  const fallbackKey = `CHAT_LIST.ATTACHMENTS.${ATTACHMENT_TYPES.FALLBACK}.CONTENT`;

  return t(te(key) ? key : fallbackKey);
});

// A citada pode viver em outra conversa (o backend resolve pelo id da mensagem,
// nao pela conversa). Nesse caso o scroll nao acha o elemento e cairia no
// scrollToBottom, entao a citacao nao deve parecer clicavel.
const canScrollToReply = computed(() => {
  const reply = inReplyTo.value;
  if (!reply?.id) return false;

  const replyConversationId = reply.conversationId ?? reply.conversation_id;
  if (replyConversationId === null || replyConversationId === undefined) {
    return true;
  }

  return Number(replyConversationId) === Number(conversationId?.value);
});

const handleReplyClick = event => {
  // Algumas bolhas tem @click proprio no root (Image abre a galeria); clicar na
  // citacao nao pode disparar a acao da bolha inteira.
  event?.stopPropagation?.();
  if (!canScrollToReply.value) return;

  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: inReplyTo.value.id,
  });
};
</script>

<template>
  <div
    class="text-sm"
    :class="[
      messageClass,
      {
        'max-w-lg': variant !== MESSAGE_VARIANTS.EMAIL,
      },
    ]"
  >
    <div
      v-if="inReplyTo"
      data-testid="message-reply-preview"
      class="flex gap-2 items-start p-2 -mx-1 mb-2 rounded-lg bg-n-alpha-black1"
      :class="{ 'cursor-pointer': canScrollToReply }"
      @click="handleReplyClick"
    >
      <div
        v-if="showThumbnail"
        class="overflow-hidden relative rounded-md shrink-0"
      >
        <img
          :src="replyThumbnailUrl"
          :alt="replyToLabel"
          data-testid="message-reply-thumbnail"
          class="object-cover rounded-md size-11 skip-context-menu"
          @error="hasThumbnailError = true"
        />
        <span
          v-if="isVideoThumbnail"
          class="flex absolute inset-0 justify-center items-center bg-n-slate-12/30"
        >
          <Icon icon="i-lucide-play" class="size-4 text-white" />
        </span>
      </div>
      <div class="flex-1 min-w-0">
        <div
          v-if="replySenderName"
          data-testid="message-reply-sender"
          class="font-medium truncate"
        >
          {{ replySenderName }}
        </div>
        <div
          v-if="replyToPreview"
          v-dompurify-html="replyToPreview"
          class="prose prose-bubble line-clamp-2"
        />
        <div v-else class="flex gap-1 items-center min-w-0">
          <Icon v-if="replyIcon" :icon="replyIcon" class="shrink-0 size-3.5" />
          <span class="truncate">{{ replyToLabel }}</span>
        </div>
      </div>
    </div>
    <slot />
    <MessageMeta
      v-if="shouldShowMeta"
      :class="[
        flexOrientationClass,
        variant === MESSAGE_VARIANTS.EMAIL ? 'px-3 pb-3' : '',
        variant === MESSAGE_VARIANTS.PRIVATE
          ? 'text-n-amber-12/50'
          : 'text-n-slate-11',
      ]"
      class="mt-2"
    />
  </div>
</template>
